/**
 * Serial Connection - Web Serial API wrapper for STAK MIDI debug CLI
 *
 * Handles connection to the master module's debug/programming USB port
 * for configuration upload and device management.
 */

import {
  FirmwareStatus,
  InstalledFirmwareInfo,
  FirmwareVersion,
  parseVersionFromHex,
  formatVersionHex,
} from './firmwareUpdate';

// Web Serial API types (not yet in standard TypeScript lib)
declare global {
  interface Navigator {
    serial: Serial;
  }

  interface Serial {
    requestPort(options?: SerialPortRequestOptions): Promise<SerialPort>;
    getPorts(): Promise<SerialPort[]>;
  }

  interface SerialPortRequestOptions {
    filters?: SerialPortFilter[];
  }

  interface SerialPortFilter {
    usbVendorId?: number;
    usbProductId?: number;
  }

  interface SerialPort extends EventTarget {
    readable: ReadableStream<Uint8Array> | null;
    writable: WritableStream<Uint8Array> | null;
    open(options: SerialOptions): Promise<void>;
    close(): Promise<void>;
    ondisconnect: ((this: SerialPort, ev: Event) => void) | null;
  }

  interface SerialOptions {
    baudRate: number;
    dataBits?: number;
    stopBits?: number;
    parity?: 'none' | 'even' | 'odd';
    bufferSize?: number;
    flowControl?: 'none' | 'hardware';
  }
}

/** Connection state */
export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

/** Parsed response from CLI */
export interface CliResponse {
  success: boolean;
  message: string;
  raw: string;
}

/** Device info from 'version' and 'status' commands */
export interface DeviceInfo {
  firmware: string;
  build: string;
  mcu: string;
  uptime: number;
  modulesRegistered: number;
  modulesOnline: number;
  configLoaded: boolean;
  gridCols?: number;
  gridRows?: number;
  totalKeys?: number;
}

/** Serial connection callback options */
export interface ConnectionOptions {
  baudRate: number;
}

/** Callback types */
type DataCallback = (data: string) => void;
type StateCallback = (state: ConnectionState) => void;

const DEFAULT_BAUD_RATE = 115200;
const READ_TIMEOUT_MS = 5000;
const BINARY_CHUNK_SIZE = 512;
const MAX_READ_BUFFER = 4096; // Cap readBuffer at 4KB — command responses are small; a large
                               // buffer causes costly string copies during key event streaming

/**
 * SerialConnection class - manages Web Serial API connection
 */
export class SerialConnection {
  private port: SerialPort | null = null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private writer: WritableStreamDefaultWriter<Uint8Array> | null = null;
  private readBuffer = '';
  private state: ConnectionState = 'disconnected';
  private baudRate: number;
  private readLoopRunning = false;
  private encoder = new TextEncoder();
  private decoder = new TextDecoder();
  private commandLock: Promise<void> = Promise.resolve();

  // Support multiple listeners
  private dataCallbacks: Set<DataCallback> = new Set();
  private stateCallbacks: Set<StateCallback> = new Set();

  // Event-driven data arrival notification for waitForPattern
  private dataArrivedResolve: (() => void) | null = null;

  constructor(options: Partial<ConnectionOptions> = {}) {
    this.baudRate = options.baudRate ?? DEFAULT_BAUD_RATE;
  }

  /** Subscribe to data events - returns unsubscribe function */
  onData(callback: DataCallback): () => void {
    this.dataCallbacks.add(callback);
    return () => this.dataCallbacks.delete(callback);
  }

  /** Subscribe to state changes - returns unsubscribe function */
  onStateChange(callback: StateCallback): () => void {
    this.stateCallbacks.add(callback);
    // Immediately notify of current state
    callback(this.state);
    return () => this.stateCallbacks.delete(callback);
  }

  /** Legacy method for backwards compatibility - deprecated, use onData/onStateChange */
  setCallbacks(onData?: DataCallback, onStateChange?: StateCallback): () => void {
    if (onData) this.dataCallbacks.add(onData);
    if (onStateChange) this.stateCallbacks.add(onStateChange);
    return () => {
      if (onData) this.dataCallbacks.delete(onData);
      if (onStateChange) this.stateCallbacks.delete(onStateChange);
    };
  }

  /** Check if Web Serial API is supported */
  static isSupported(): boolean {
    return 'serial' in navigator;
  }

  /** Get current connection state */
  getState(): ConnectionState {
    return this.state;
  }

  /** Check if connected */
  isConnected(): boolean {
    return this.state === 'connected';
  }

  private setState(state: ConnectionState) {
    this.state = state;
    // Notify all listeners
    this.stateCallbacks.forEach(cb => cb(state));
  }

  private emitData(data: string) {
    this.dataCallbacks.forEach(cb => cb(data));
  }

  /** Handle USB disconnect event from the port (device power-cycle or cable unplug) */
  private handlePortDisconnect = () => {
    this.readLoopRunning = false;
    // Release resources without trying to close the now-dead port
    try { this.reader?.releaseLock(); } catch { /* already gone */ }
    try { this.writer?.releaseLock(); } catch { /* already gone */ }
    this.reader = null;
    this.writer = null;
    this.port = null;
    this.readBuffer = '';
    this.setState('disconnected');
  };

  /**
   * Request and connect to a serial port
   * User will see a browser dialog to select the port
   */
  async connect(): Promise<boolean> {
    if (!SerialConnection.isSupported()) {
      throw new Error('Web Serial API not supported. Use Chrome, Edge, or Opera.');
    }

    if (this.state === 'connected') {
      return true;
    }

    if (this.state === 'connecting') {
      return false;
    }

    // Clean up stale resources from a previous connection (e.g. after device power-cycle)
    if (this.port) {
      await this.disconnect();
    }

    try {
      this.setState('connecting');

      // Request port from user
      this.port = await navigator.serial.requestPort();

      // Listen for USB disconnect (device power-cycle, cable unplug)
      this.port.addEventListener('disconnect', this.handlePortDisconnect);

      // Open with configured baud rate and larger buffer to prevent overruns
      await this.port.open({
        baudRate: this.baudRate,
        bufferSize: 16384,  // 16KB buffer to handle burst data from device
      });

      // Get reader and writer
      if (this.port.readable && this.port.writable) {
        this.writer = this.port.writable.getWriter();
        this.startReadLoop();
        this.setState('connected');
        return true;
      } else {
        throw new Error('Port not readable/writable');
      }
    } catch (err) {
      this.setState('error');
      if (err instanceof Error && err.name === 'NotFoundError') {
        // User cancelled the dialog
        this.setState('disconnected');
        return false;
      }
      throw err;
    }
  }

  /**
   * Disconnect from the serial port
   */
  async disconnect(): Promise<void> {
    this.readLoopRunning = false;

    if (this.reader) {
      try {
        await this.reader.cancel();
      } catch {
        // Ignore cancel errors
      }
      this.reader.releaseLock();
      this.reader = null;
    }

    if (this.writer) {
      try {
        await this.writer.close();
      } catch {
        // Ignore close errors
      }
      try {
        this.writer.releaseLock();
      } catch {
        // Ignore releaseLock errors (stream may be errored on USB disconnect)
      }
      this.writer = null;
    }

    if (this.port) {
      this.port.removeEventListener('disconnect', this.handlePortDisconnect);
      try {
        await this.port.close();
      } catch {
        // Ignore close errors
      }
      this.port = null;
    }

    this.readBuffer = '';
    this.setState('disconnected');
  }

  /**
   * Start background read loop
   */
  private async startReadLoop(): Promise<void> {
    if (!this.port?.readable) return;

    this.readLoopRunning = true;
    this.reader = this.port.readable.getReader();

    try {
      while (this.readLoopRunning) {
        const { value, done } = await this.reader.read();
        if (done) break;
        if (value) {
          const text = this.decoder.decode(value, { stream: true });
          this.readBuffer += text;
          // Cap buffer to prevent unbounded memory growth during long sessions
          if (this.readBuffer.length > MAX_READ_BUFFER) {
            this.readBuffer = this.readBuffer.slice(-MAX_READ_BUFFER);
          }
          if (this.dataArrivedResolve) {
            this.dataArrivedResolve();
            this.dataArrivedResolve = null;
          }
          this.emitData(text);
        }
      }
    } catch (err) {
      if (this.readLoopRunning) {
        console.error('Serial read error:', err);
        this.setState('error');
      }
    } finally {
      this.reader?.releaseLock();
      this.reader = null;
    }
  }

  /**
   * Send raw string to device
   */
  async send(data: string): Promise<void> {
    if (!this.writer) {
      throw new Error('Not connected');
    }
    await this.writer.write(this.encoder.encode(data));
  }

  /**
   * Send raw bytes to device
   */
  async sendBytes(data: Uint8Array): Promise<void> {
    if (!this.writer) {
      throw new Error('Not connected');
    }
    await this.writer.write(data);
  }

  /**
   * Clear the read buffer
   */
  clearBuffer(): void {
    this.readBuffer = '';
  }

  /**
   * Wait for new data to arrive from the serial port
   */
  private waitForData(): Promise<void> {
    return new Promise(resolve => {
      this.dataArrivedResolve = resolve;
    });
  }

  /**
   * Wait for a specific pattern in the response
   */
  async waitForPattern(pattern: RegExp | string, timeoutMs = READ_TIMEOUT_MS): Promise<string> {
    const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
    const deadline = Date.now() + timeoutMs;

    while (Date.now() < deadline) {
      if (regex.test(this.readBuffer)) {
        return this.readBuffer;
      }
      const remaining = deadline - Date.now();
      if (remaining <= 0) break;
      await Promise.race([
        this.waitForData(),
        this.sleep(remaining),
      ]);
    }

    throw new Error(`Timeout waiting for pattern: ${pattern}`);
  }

  /**
   * Wait for the CLI prompt
   */
  async waitForPrompt(timeoutMs = READ_TIMEOUT_MS): Promise<string> {
    return this.waitForPattern(/> $/, timeoutMs);
  }

  /**
   * Send a CLI command and wait for response.
   * Commands are serialized via a mutex to prevent interleaved responses
   * when multiple callers (or streaming data) share the readBuffer.
   */
  async sendCommand(command: string, timeoutMs = READ_TIMEOUT_MS): Promise<CliResponse> {
    // Queue behind any in-flight command to prevent buffer interleaving
    const result = this.commandLock.then(async (): Promise<CliResponse> => {
      this.clearBuffer();
      await this.send(command + '\r\n');

      try {
        const response = await this.waitForPrompt(timeoutMs);

        // Parse response
        const lines = response.split('\r\n').filter(l => l.trim());
        const hasOk = lines.some(l => l.startsWith('OK:'));
        const hasErr = lines.some(l => l.startsWith('ERR:'));

        return {
          success: hasOk || (!hasErr && lines.length > 0),
          message: lines.find(l => l.startsWith('OK:') || l.startsWith('ERR:')) || '',
          raw: response,
        };
      } catch (err) {
        return {
          success: false,
          message: err instanceof Error ? err.message : 'Unknown error',
          raw: this.readBuffer,
        };
      }
    });

    // Chain lock: next command waits for this one (swallow errors to keep chain alive)
    this.commandLock = result.then(() => {}, () => {});

    return result;
  }

  /**
   * Get device info (version + status)
   */
  async getDeviceInfo(): Promise<DeviceInfo> {
    const versionResp = await this.sendCommand('version');
    const statusResp = await this.sendCommand('status');

    const info: DeviceInfo = {
      firmware: 'Unknown',
      build: 'Unknown',
      mcu: 'Unknown',
      uptime: 0,
      modulesRegistered: 0,
      modulesOnline: 0,
      configLoaded: false,
    };

    // Parse version response
    const firmwareMatch = versionResp.raw.match(/Firmware:\s*(.+)/);
    if (firmwareMatch?.[1]) info.firmware = firmwareMatch[1].trim();

    const buildMatch = versionResp.raw.match(/Build:\s*(.+)/);
    if (buildMatch?.[1]) info.build = buildMatch[1].trim();

    const mcuMatch = versionResp.raw.match(/MCU:\s*(.+)/);
    if (mcuMatch?.[1]) info.mcu = mcuMatch[1].trim();

    // Parse status response
    const uptimeMatch = statusResp.raw.match(/Uptime:\s*(\d+)/);
    if (uptimeMatch?.[1]) info.uptime = parseInt(uptimeMatch[1], 10);

    const modulesMatch = statusResp.raw.match(/Modules:\s*(\d+)\s*registered,\s*(\d+)\s*online/);
    if (modulesMatch?.[1] && modulesMatch[2]) {
      info.modulesRegistered = parseInt(modulesMatch[1], 10);
      info.modulesOnline = parseInt(modulesMatch[2], 10);
    }

    const configMatch = statusResp.raw.match(/Config:\s*(\w+)/);
    if (configMatch?.[1]) info.configLoaded = configMatch[1] === 'loaded';

    const gridMatch = statusResp.raw.match(/Grid:\s*(\d+)x(\d+)\s*modules\s*\((\d+)\s*keys\)/);
    if (gridMatch?.[1] && gridMatch[2] && gridMatch[3]) {
      info.gridCols = parseInt(gridMatch[1], 10);
      info.gridRows = parseInt(gridMatch[2], 10);
      info.totalKeys = parseInt(gridMatch[3], 10);
    }

    return info;
  }

  /**
   * Upload BRI configuration binary
   */
  async uploadConfig(
    data: Uint8Array,
    onProgress?: (percent: number) => void
  ): Promise<CliResponse> {
    // Step 1: Send upload command
    this.clearBuffer();
    await this.send(`config upload ${data.length}\r\n`);

    // Step 2: Wait for "OK: Ready to receive" or error
    try {
      await this.waitForPattern(/OK: Ready to receive|ERR:/, 5000);
    } catch {
      return {
        success: false,
        message: 'Device not ready for upload',
        raw: this.readBuffer,
      };
    }

    if (this.readBuffer.includes('ERR:')) {
      return {
        success: false,
        message: this.readBuffer.match(/ERR:\s*(.+)/)?.[1] || 'Upload rejected',
        raw: this.readBuffer,
      };
    }

    // Step 3: Send binary data in chunks
    // Device prints '.' every 1KB received - use dots as flow control
    this.clearBuffer();
    let sent = 0;
    let dotsReceived = 0;

    while (sent < data.length) {
      const chunk = data.slice(sent, sent + BINARY_CHUNK_SIZE);
      await this.sendBytes(chunk);
      sent += chunk.length;

      // Wait for device to confirm receipt via progress dots
      const expectedDots = Math.floor(sent / 1024);
      if (expectedDots > dotsReceived) {
        const deadline = Date.now() + 5000;
        while (dotsReceived < expectedDots && Date.now() < deadline) {
          await this.sleep(10);
          const newDots = (this.readBuffer.match(/\./g) || []).length;
          if (newDots > dotsReceived) {
            dotsReceived = newDots;
          }
        }
        const bytesReceived = dotsReceived * 1024;
        onProgress?.(Math.round((bytesReceived / data.length) * 100));
      }
    }

    // Step 4: Wait for completion
    // Wait for OK/ERR rather than prompt — the device prints a `> ` prompt
    // right after "Send binary data now..." which can match waitForPrompt
    // before the actual result arrives.
    try {
      await this.waitForPattern(/OK:|ERR:/, 30000);

      const hasOk = this.readBuffer.includes('OK:');
      const hasErr = this.readBuffer.includes('ERR:');

      return {
        success: hasOk && !hasErr,
        message: this.readBuffer.match(/OK:\s*(.+)|ERR:\s*(.+)/)?.[0] || '',
        raw: this.readBuffer,
      };
    } catch {
      return {
        success: false,
        message: 'Timeout waiting for upload completion',
        raw: this.readBuffer,
      };
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Upload firmware binary for update
   * Protocol:
   * 1. Send "fwupdate upload <size> <version_hex>"
   * 2. Wait for "OK: Ready to receive"
   * 3. Send raw binary data
   * 4. Wait for completion response
   */
  async uploadFirmware(
    data: Uint8Array,
    version: FirmwareVersion,
    onProgress?: (percent: number, bytesReceived: number) => void
  ): Promise<CliResponse> {
    const versionHex = formatVersionHex(version);

    // Step 1: Send upload command
    this.clearBuffer();
    await this.send(`fwupdate upload ${data.length} ${versionHex}\r\n`);

    // Step 2: Wait for "OK: Ready to receive" or error
    try {
      await this.waitForPattern(/OK: Ready to receive|ERR:/, 5000);
    } catch {
      return {
        success: false,
        message: 'Device not ready for firmware upload',
        raw: this.readBuffer,
      };
    }

    if (this.readBuffer.includes('ERR:')) {
      const errMatch = this.readBuffer.match(/ERR:\s*(.+)/);
      return {
        success: false,
        message: errMatch?.[1] || 'Firmware upload rejected',
        raw: this.readBuffer,
      };
    }

    // Step 3: Send binary data in chunks
    // Device prints '.' every 1KB received - use dots as flow control
    this.clearBuffer();
    let sent = 0;
    let dotsReceived = 0;

    while (sent < data.length) {
      const chunk = data.slice(sent, sent + BINARY_CHUNK_SIZE);
      await this.sendBytes(chunk);
      sent += chunk.length;

      // Every 2KB sent, wait for device to confirm receipt via progress dot.
      // At 115200 baud the device ring buffer is limited, so we must not
      // send faster than the device can drain.  Each dot = 1KB received.
      const expectedDots = Math.floor(sent / 1024);
      if (expectedDots > dotsReceived) {
        const deadline = Date.now() + 5000;
        while (dotsReceived < expectedDots && Date.now() < deadline) {
          await this.sleep(10);
          const newDots = (this.readBuffer.match(/\./g) || []).length;
          if (newDots > dotsReceived) {
            dotsReceived = newDots;
          }
        }
        // Report progress
        const bytesReceived = dotsReceived * 1024;
        const percent = Math.min(99, Math.round((bytesReceived / data.length) * 100));
        onProgress?.(percent, bytesReceived);
      }
    }

    // Step 4: Wait for completion (CRC calculation and flash write takes time)
    try {
      // Firmware upload completion can take 30+ seconds for large files
      // (CRC calc + flash erase + flash write)
      await this.waitForPattern(/OK:|ERR:/, 60000);

      const hasOk = this.readBuffer.includes('OK:');
      const hasErr = this.readBuffer.includes('ERR:');

      if (hasOk && !hasErr) {
        onProgress?.(100, data.length);
      }

      return {
        success: hasOk && !hasErr,
        message: this.readBuffer.match(/OK:\s*(.+)|ERR:\s*(.+)/)?.[0] || '',
        raw: this.readBuffer,
      };
    } catch {
      return {
        success: false,
        message: 'Timeout waiting for firmware upload completion',
        raw: this.readBuffer,
      };
    }
  }

  /**
   * Get firmware update status (pending update info)
   */
  async getFirmwareStatus(): Promise<FirmwareStatus> {
    const resp = await this.sendCommand('fwupdate status', 5000);

    const status: FirmwareStatus = {
      hasPendingUpdate: false,
    };

    // Check if there's a pending update
    if (resp.raw.includes('No pending update') || resp.raw.includes('No valid update')) {
      return status;
    }

    // Parse header info
    const magicMatch = resp.raw.match(/Magic:\s*(0x[0-9a-fA-F]+)/);
    if (magicMatch?.[1]) {
      status.magic = parseInt(magicMatch[1], 16);
    }

    const headerVerMatch = resp.raw.match(/Header version:\s*(\d+)/);
    if (headerVerMatch?.[1]) {
      status.headerVersion = parseInt(headerVerMatch[1], 10);
    }

    const sizeMatch = resp.raw.match(/Size:\s*(\d+)/);
    if (sizeMatch?.[1]) {
      status.size = parseInt(sizeMatch[1], 10);
    }

    const crcMatch = resp.raw.match(/CRC:\s*(0x[0-9a-fA-F]+)/);
    if (crcMatch?.[1]) {
      status.crc = parseInt(crcMatch[1], 16);
    }

    const versionMatch = resp.raw.match(/Version:\s*(0x[0-9a-fA-F]+)/);
    if (versionMatch?.[1]) {
      const versionHex = parseInt(versionMatch[1], 16);
      status.version = parseVersionFromHex(versionHex);
    }

    const validMatch = resp.raw.match(/Valid:\s*(\d+)/);
    if (validMatch?.[1]) {
      status.hasPendingUpdate = validMatch[1] === '1';
    }

    return status;
  }

  /**
   * Get installed firmware info
   */
  async getInstalledFirmwareInfo(): Promise<InstalledFirmwareInfo> {
    const resp = await this.sendCommand('fwupdate info', 5000);

    const info: InstalledFirmwareInfo = {};

    // Check for "first boot" or no info
    if (resp.raw.includes('first boot') || resp.raw.includes('No firmware info')) {
      return info;
    }

    const magicMatch = resp.raw.match(/Magic:\s*(0x[0-9a-fA-F]+)/);
    if (magicMatch?.[1]) {
      info.magic = parseInt(magicMatch[1], 16);
    }

    const versionMatch = resp.raw.match(/Version:\s*(0x[0-9a-fA-F]+)/);
    if (versionMatch?.[1]) {
      const versionHex = parseInt(versionMatch[1], 16);
      info.version = parseVersionFromHex(versionHex);
    }

    const sizeMatch = resp.raw.match(/Size:\s*(\d+)/);
    if (sizeMatch?.[1]) {
      info.size = parseInt(sizeMatch[1], 10);
    }

    const crcMatch = resp.raw.match(/CRC:\s*(0x[0-9a-fA-F]+)/);
    if (crcMatch?.[1]) {
      info.crc = parseInt(crcMatch[1], 16);
    }

    const countMatch = resp.raw.match(/Install count:\s*(\d+)/);
    if (countMatch?.[1]) {
      info.installCount = parseInt(countMatch[1], 10);
    }

    return info;
  }

  /**
   * Trigger firmware update (reboot to bootloader)
   * WARNING: This will reboot the device!
   */
  async triggerFirmwareUpdate(): Promise<CliResponse> {
    // Send trigger command - device will reboot, so we won't get a normal response
    this.clearBuffer();
    await this.send('fwupdate trigger\r\n');

    // Wait briefly to see if we get an error (e.g., no pending update)
    await this.sleep(500);

    if (this.readBuffer.includes('ERR:')) {
      const errMatch = this.readBuffer.match(/ERR:\s*(.+)/);
      return {
        success: false,
        message: errMatch?.[1] || 'Failed to trigger update',
        raw: this.readBuffer,
      };
    }

    // If we get here, device is likely rebooting
    return {
      success: true,
      message: 'Device rebooting to apply update...',
      raw: this.readBuffer,
    };
  }

  /**
   * Abort pending firmware update
   */
  async abortFirmwareUpdate(): Promise<CliResponse> {
    return this.sendCommand('fwupdate abort', 5000);
  }
}

/**
 * Parse a JSON object from a CLI response string.
 * Finds the first '{' and matches braces to extract the complete JSON object.
 */
export function parseJsonFromCliResponse(raw: string): unknown | null {
  const startIdx = raw.indexOf('{');
  if (startIdx === -1) return null;

  let depth = 0;
  let endIdx = -1;
  for (let i = startIdx; i < raw.length; i++) {
    if (raw[i] === '{') depth++;
    else if (raw[i] === '}') {
      depth--;
      if (depth === 0) {
        endIdx = i;
        break;
      }
    }
  }

  if (endIdx === -1) return null;

  try {
    return JSON.parse(raw.substring(startIdx, endIdx + 1));
  } catch {
    return null;
  }
}

/** Singleton instance for easy access */
let defaultConnection: SerialConnection | null = null;

export function getSerialConnection(): SerialConnection {
  if (!defaultConnection) {
    defaultConnection = new SerialConnection();
  }
  return defaultConnection;
}
